import { styled, Button, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import Avatar from 'further-ui/components/Avatar';
import { Column, Row } from 'further-ui/components/Layout';
import { useLaunchPortalImpersonation } from 'hooks/data/impersonationAccess/useLaunchPortalImpersonation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { CalendarTime } from 'further-ui/utils';
import {
  OnboardingStep,
  OnboardingStepStatus,
} from 'further-types/lp/onboarding';
import { match } from 'ts-pattern';
import { PropsWithChildren } from 'react';

const ReviewEntry = styled(Row)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.sm),
}));

const NewTabIcon = styled(OpenInNew)(({ theme }) => ({
  color: theme.palette.common.white,
}));

const AdminMoreInfoRequest = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.xl),
}));

type Props = {
  firmDomain: string;
  isFirstEntry: boolean;
  application: {
    id: string;
    vehicleId: string;
    investorId: string;
  };
  step: OnboardingStep;
  entry: {
    status: OnboardingStepStatus;
    isApproval?: boolean;
    changedBy?: {
      firstName?: string;
      lastName?: string;
      profilePic?: string;
    };
    changedByRef?: 'furtherUser' | 'adminUser';
    changedAt: CalendarTime;
    message?: string;
  };
};

const JourneyStepChangelogEntry: React.FC<PropsWithChildren<Props>> = ({
  entry,
  firmDomain,
  application,
  step,
  isFirstEntry,
  children,
}) => {
  const [canImpersonate] = useHasPermissions([
    RolePermission.UseImpersonationAccessInvestor,
  ]);
  const { vehicleId, investorId, id: vehicleApplicationId } = application;

  const { investor } = useLaunchPortalImpersonation(
    `/lp-onboarding/${vehicleId}/${vehicleApplicationId}/review?step=${step}`,
  );

  const userFullName = entry.changedBy
    ? `${entry.changedBy.firstName ?? ''} ${
        entry.changedBy.lastName ?? ''
      }`.trim()
    : '';

  const handleViewResponse = () => {
    investor({
      firmDomain,
      investorId,
    });
  };

  const entryText = match(entry)
    .with({ status: OnboardingStepStatus.MoreInformationRequested }, () => {
      return `${userFullName} requested additional information on`;
    })
    .with({ status: OnboardingStepStatus.Completed }, () => {
      return entry.isApproval
        ? `Approved by ${userFullName} on`
        : `${userFullName} completed on`;
    })
    .with(
      { status: OnboardingStepStatus.InReview, changedByRef: 'furtherUser' },
      () => `${userFullName} completed on`,
    )
    .with(
      { status: OnboardingStepStatus.InReview, changedByRef: 'adminUser' },
      () => `${userFullName} marked as in review on`,
    )
    .with(
      { status: OnboardingStepStatus.StartedByInvestor },
      () => `${userFullName} started on`,
    )
    .otherwise(() => '');

  if (isFirstEntry) {
    return (
      <Column fullWidth spacing="sm">
        <Row centered fullWidth spacing="sm" justify="space-between">
          <ReviewEntry centered spacing="xs">
            <Avatar color="random" text={userFullName} />
            <Typography variant="h6">
              {entryText} {entry.changedAt.toReadableWithTime()}
            </Typography>
          </ReviewEntry>
          {canImpersonate ? (
            <Button
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<NewTabIcon fontSize="small" />}
              onClick={handleViewResponse}
            >
              View response
            </Button>
          ) : null}
        </Row>
        {children}
      </Column>
    );
  }

  const entryDetails = match(entry.status)
    .with(OnboardingStepStatus.MoreInformationRequested, () => {
      return entry.message ? (
        <AdminMoreInfoRequest variant="body2" color="grey.600">
          "{entry.message}"
        </AdminMoreInfoRequest>
      ) : null;
    })
    .otherwise(() => null);

  return (
    <ReviewEntry>
      <Column fullWidth spacing="xs">
        <Row centered spacing="xs">
          <Avatar
            src={entry.changedBy?.profilePic}
            color="random"
            text={userFullName}
          />
          <Typography variant="h6">
            {entryText} {entry.changedAt.toReadableWithTime()}
          </Typography>
        </Row>
        {entryDetails ? <Row>{entryDetails}</Row> : null}
      </Column>
    </ReviewEntry>
  );
};

export default JourneyStepChangelogEntry;
